import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';

import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const STEPS = [
  {
    number: '01',
    Icon: PersonAddRoundedIcon,
    title: 'Regístrate',
    description: 'Crea tu cuenta y configura tu organización en minutos.',
    gradient: 'linear-gradient(135deg, #18586b 0%, #0f4c75 100%)',
    glow: 'rgba(24,88,107,0.3)',
  },
  {
    number: '02',
    Icon: SettingsRoundedIcon,
    title: 'Configura tus productos',
    description: 'Define tipos de préstamos, tasas de interés, plazos y estructuras de pago.',
    gradient: 'linear-gradient(135deg, #55b4ac 0%, #18586b 100%)',
    glow: 'rgba(85,180,172,0.3)',
  },
  {
    number: '03',
    Icon: Inventory2RoundedIcon,
    title: 'Agrega inventario',
    description: 'Importa tus activos o productos para comenzar a operar.',
    gradient: 'linear-gradient(135deg, #0f4c75 0%, #55b4ac 100%)',
    glow: 'rgba(15,76,117,0.3)',
  },
  {
    number: '04',
    Icon: TrendingUpRoundedIcon,
    title: 'Presta y cobra',
    description: 'Procesa préstamos y gestiona pagos a través de nuestro sistema optimizado.',
    gradient: 'linear-gradient(135deg, #18586b 0%, #55b4ac 100%)',
    glow: 'rgba(85,180,172,0.3)',
  },
];

export default function HowItWorks() {
  return (
    <Box
      id="how-it-works"
      sx={{
        py: { xs: 10, sm: 14 },
        background: 'linear-gradient(180deg, #f8fffe 0%, #eef8f7 50%, #f8fffe 100%)',
        position: 'relative',
        overflow: 'hidden',
        /* decorative arc */
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '120%',
          height: '60%',
          background: 'radial-gradient(ellipse, rgba(85,180,172,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Section header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 7, sm: 9 },
            animation: `${fadeInUp} 0.8s ease-out`,
          }}
        >
          <Chip
            label="Proceso"
            size="small"
            sx={{
              mb: 2,
              background: 'rgba(85,180,172,0.12)',
              border: '1px solid rgba(85,180,172,0.35)',
              color: '#18586b',
              fontWeight: 600,
              fontSize: '0.78rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          />
          <Typography
            component="h2"
            variant="h3"
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.6rem', md: '3rem' },
              letterSpacing: '-0.025em',
            }}
          >
            Cómo funciona
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              maxWidth: 480,
              mx: 'auto',
            }}
          >
            Comienza a operar en cuatro pasos simples
          </Typography>
        </Box>

        {/* Step cards */}
        <Grid container spacing={3} alignItems="stretch">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.number}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    background: '#ffffff',
                    border: '1px solid rgba(85,180,172,0.15)',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.35s ease',
                    animation: `${fadeInUp} 0.75s ease-out ${i * 0.12}s both`,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 16px 48px ${step.glow}, 0 4px 16px rgba(0,0,0,0.08)`,
                      borderColor: 'rgba(85,180,172,0.4)',
                    },
                    /* gradient top bar */
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: step.gradient,
                    },
                  }}
                >
                  {/* Ghost step number */}
                  <Typography
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      bottom: -12,
                      right: 8,
                      fontSize: '7rem',
                      fontWeight: 900,
                      lineHeight: 1,
                      background: step.gradient,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      opacity: 0.08,
                      userSelect: 'none',
                      letterSpacing: '-0.04em',
                    }}
                  >
                    {step.number}
                  </Typography>

                  <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                    {/* Icon badge */}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 52,
                        height: 52,
                        borderRadius: 2.5,
                        background: step.gradient,
                        boxShadow: `0 6px 20px ${step.glow}`,
                        mb: 2.5,
                      }}
                    >
                      <step.Icon sx={{ fontSize: 26, color: 'white' }} />
                    </Box>

                    {/* Step label */}
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: '#55b4ac',
                        mb: 0.75,
                      }}
                    >
                      Paso {step.number}
                    </Typography>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontSize: { xs: '1.05rem', sm: '1.1rem' },
                        mb: 1.25,
                        lineHeight: 1.3,
                      }}
                    >
                      {step.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.65,
                        fontSize: '0.875rem',
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Arrow connector — desktop only, not after last card */}
              {i < STEPS.length - 1 && (
                <Grid
                  item
                  md="auto"
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    px: 0,
                    mx: -1.5,
                  }}
                >
                  <EastRoundedIcon
                    sx={{
                      color: 'rgba(85,180,172,0.45)',
                      fontSize: 22,
                    }}
                  />
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
